// Bridges the native Sulu media selection overlay to iframe-based field
// editors (framefield). Exposes window.mavuMediaPicker.open(options), which
// resolves with the selected media (array when multiple, single object
// otherwise) or null when the user closes the overlay without confirming.
//
// options: {locale: string, multiple: boolean, types: string[], excludedIds: number[]}
import React from 'react';
import ReactDOM from 'react-dom';
import {action, observable, toJS} from 'mobx';
import {observer} from 'mobx-react';
import {MultiMediaSelectionOverlay, SingleMediaSelectionOverlay} from 'sulu-media-bundle/containers';

let requestCounter = 0;

@observer
class MediaPickerHost extends React.Component {
    @observable.ref request = null;

    open = (options = {}) => {
        return new Promise((resolve) => {
            this.startRequest(options, resolve);
        });
    };

    @action startRequest = (options, resolve) => {
        if (this.request) {
            this.request.resolve(null);
        }

        this.request = {
            id: ++requestCounter,
            locale: observable.box(options.locale || 'en'),
            multiple: !!options.multiple,
            types: options.types || [],
            excludedIds: options.excludedIds || [],
            resolve,
        };
    };

    @action finishRequest = (result) => {
        if (!this.request) {
            return;
        }

        this.request.resolve(result);
        this.request = null;
    };

    handleClose = () => {
        this.finishRequest(null);
    };

    handleConfirm = (selectedMedia) => {
        this.finishRequest(toJS(selectedMedia));
    };

    render() {
        const request = this.request;

        if (!request) {
            return null;
        }

        const Overlay = request.multiple ? MultiMediaSelectionOverlay : SingleMediaSelectionOverlay;

        return (
            <Overlay
                excludedIds={request.excludedIds}
                key={request.id}
                locale={request.locale}
                onClose={this.handleClose}
                onConfirm={this.handleConfirm}
                open={true}
                types={request.types}
            />
        );
    }
}

function mountMediaPicker() {
    if (window.mavuMediaPicker) {
        return;
    }

    const container = document.createElement('div');
    container.setAttribute('data-mavu-media-picker', '');
    document.body.appendChild(container);

    const ref = React.createRef();
    ReactDOM.render(<MediaPickerHost ref={ref} />, container);

    window.mavuMediaPicker = {
        open: (options) => {
            if (!ref.current) {
                return Promise.resolve(null);
            }

            return ref.current.open(options);
        },
    };
}

if (document.body) {
    mountMediaPicker();
} else {
    document.addEventListener('DOMContentLoaded', mountMediaPicker);
}
